import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Ellipsis,
  KeyRound,
  Lock,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserCheck,
  UserPlus,
  UserX,
  Users
} from 'lucide-react'
import { Role } from '@shared/types'
import type { UserDTO } from '@shared/types'
import InviteUserDialog, { SecretReveal, generatePassword } from '@/components/admin/InviteUserDialog'
import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  EmptyState,
  IconButton,
  Modal,
  Skeleton,
  Spinner,
  Tooltip,
  useToast
} from '@/components/ui'
import type { DropdownMenuItem } from '@/components/ui'
import { api, errorMessage } from '@/lib/api'
import { formatNumber, pluralize, relativeTime } from '@/lib/format'
import { useAuthStore } from '@/stores/useAuthStore'

/* The admin routes are written by another part of the app; accept both a bare
   array and a `{ users }` envelope rather than break on the shape. */
function toUserList(payload: unknown): UserDTO[] {
  if (Array.isArray(payload)) return payload as UserDTO[]
  const wrapped = (payload as { users?: unknown } | null)?.users
  return Array.isArray(wrapped) ? (wrapped as UserDTO[]) : []
}

function toUser(payload: unknown): UserDTO | null {
  if (!payload || typeof payload !== 'object') return null
  const wrapped = (payload as { user?: unknown }).user
  const candidate = wrapped && typeof wrapped === 'object' ? wrapped : payload
  return typeof (candidate as UserDTO).id === 'string' ? (candidate as UserDTO) : null
}

function readPassword(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const value = (payload as { password?: unknown }).password
  return typeof value === 'string' && value.length > 0 ? value : null
}

function displayName(user: UserDTO): string {
  return user.name.trim() || user.email
}

/** The single sentence that explains why a row's destructive actions are off. */
function restrictionFor(isSelf: boolean, isLastAdmin: boolean): string | null {
  if (isSelf && isLastAdmin) {
    return 'This is your own account and the last active admin. Argus keeps at least one admin who can sign in, so it cannot be demoted, deactivated or deleted.'
  }
  if (isSelf) {
    return 'This is your own account. To stop you locking yourself out, you cannot demote, deactivate or delete it.'
  }
  if (isLastAdmin) {
    return 'This is the last active admin. Argus keeps at least one, so this account cannot be demoted, deactivated or deleted.'
  }
  return null
}

function RoleBadge({ role }: { role: Role }): React.ReactElement {
  return (
    <Badge size="sm" variant={role === Role.Admin ? 'accent' : 'neutral'}>
      {role === Role.Admin ? 'Admin' : 'User'}
    </Badge>
  )
}

function StateBadge({ active }: { active: boolean }): React.ReactElement {
  return (
    <Badge size="sm" variant={active ? 'success' : 'warning'}>
      {active ? 'Active' : 'Deactivated'}
    </Badge>
  )
}

interface RowModel {
  user: UserDTO
  isSelf: boolean
  restriction: string | null
  items: DropdownMenuItem[]
  busy: boolean
}

function RowActions({ row }: { row: RowModel }): React.ReactElement {
  return (
    <div className="flex items-center justify-end gap-1">
      {row.busy ? <Spinner size="sm" className="text-fg-subtle" /> : null}
      {row.restriction ? (
        <Tooltip content={row.restriction} side="left">
          <span
            tabIndex={0}
            className="grid size-8 shrink-0 place-items-center rounded-control text-fg-subtle"
          >
            <Lock className="size-3.5" aria-hidden="true" />
            <span className="sr-only">{row.restriction}</span>
          </span>
        </Tooltip>
      ) : null}
      <DropdownMenu
        align="end"
        items={row.items}
        trigger={
          <IconButton label={`Actions for ${displayName(row.user)}`} size="sm" disabled={row.busy}>
            <Ellipsis />
          </IconButton>
        }
      />
    </div>
  )
}

interface ResetPasswordDialogProps {
  user: UserDTO | null
  onClose: () => void
}

function ResetPasswordDialog({ user, onClose }: ResetPasswordDialogProps): React.ReactElement {
  const { toast } = useToast()
  const [working, setWorking] = useState(false)
  const [issued, setIssued] = useState<string | null>(null)

  useEffect(() => {
    if (!user) return
    setWorking(false)
    setIssued(null)
  }, [user])

  const reset = async (): Promise<void> => {
    if (!user) return
    const candidate = generatePassword()
    setWorking(true)
    try {
      const payload = await api.post<unknown>(`/admin/users/${user.id}/reset-password`, {
        password: candidate
      })
      // If the server mints its own password, that one is the truth.
      setIssued(readPassword(payload) ?? candidate)
    } catch (error) {
      toast({
        title: 'Could not reset the password',
        description: errorMessage(error),
        variant: 'error'
      })
    } finally {
      setWorking(false)
    }
  }

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      size="sm"
      title={issued ? 'New password' : 'Reset password?'}
      footer={
        issued ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              loading={working}
              onClick={() => {
                void reset()
              }}
            >
              Generate new password
            </Button>
          </>
        )
      }
    >
      {user === null ? null : issued ? (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-fg-muted">
            The password for{' '}
            <span className="font-semibold text-fg">{displayName(user)}</span> has been replaced.
          </p>
          <SecretReveal label="Password" value={issued} />
          <p className="text-xs leading-relaxed text-fg-subtle">
            Copy it now. Argus keeps only a hash, so this is the one and only time it can be read —
            if it is lost, reset the password again. Nothing was emailed; hand it over through a
            channel you trust.
          </p>
        </div>
      ) : (
        <div className="space-y-3 text-sm leading-relaxed text-fg-muted">
          <p>
            Argus will generate a strong random password for{' '}
            <span className="font-semibold text-fg">{displayName(user)}</span> ({user.email}).
          </p>
          <p>
            Their current password stops working straight away, and they will be asked to choose a
            new one after signing in. The generated password is shown once, here, and cannot be
            retrieved afterwards.
          </p>
        </div>
      )}
    </Modal>
  )
}

export default function UsersTable(): React.ReactElement {
  const me = useAuthStore((state) => state.user)
  const { toast } = useToast()
  const [users, setUsers] = useState<UserDTO[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<UserDTO | null>(null)
  const [resetTarget, setResetTarget] = useState<UserDTO | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setError(null)
    try {
      const payload = await api.get<unknown>('/admin/users')
      setUsers(toUserList(payload))
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const patch = useCallback(
    async (
      user: UserDTO,
      changes: Partial<Pick<UserDTO, 'role' | 'isActive'>>,
      success: string
    ): Promise<void> => {
      setBusyId(user.id)
      try {
        const payload = await api.patch<unknown>(`/admin/users/${user.id}`, changes)
        const updated = toUser(payload) ?? { ...user, ...changes }
        setUsers((current) =>
          current ? current.map((row) => (row.id === updated.id ? updated : row)) : current
        )
        toast({ title: success, variant: 'success' })
      } catch (caught) {
        toast({ title: 'Change failed', description: errorMessage(caught), variant: 'error' })
      } finally {
        setBusyId(null)
      }
    },
    [toast]
  )

  const remove = async (): Promise<void> => {
    const target = deleteTarget
    if (!target) return
    setBusyId(target.id)
    try {
      await api.del<void>(`/admin/users/${target.id}`)
      setUsers((current) => (current ? current.filter((row) => row.id !== target.id) : current))
      setDeleteTarget(null)
      toast({ title: `${displayName(target)} deleted`, variant: 'success' })
    } catch (caught) {
      toast({ title: 'Could not delete', description: errorMessage(caught), variant: 'error' })
    } finally {
      setBusyId(null)
    }
  }

  const activeAdmins = useMemo(
    () => (users ?? []).filter((user) => user.role === Role.Admin && user.isActive).length,
    [users]
  )

  const rows = useMemo<RowModel[]>(() => {
    return (users ?? []).map((user) => {
      const isSelf = user.id === me?.id
      const isLastAdmin = user.role === Role.Admin && user.isActive && activeAdmins <= 1
      const locked = isSelf || isLastAdmin
      const busy = busyId === user.id
      const label = displayName(user)

      const items: DropdownMenuItem[] = [
        user.role === Role.Admin
          ? {
              label: 'Make user',
              icon: <ShieldOff />,
              disabled: locked || busy,
              onSelect: () => {
                void patch(user, { role: Role.User }, `${label} is now a user`)
              }
            }
          : {
              label: 'Make admin',
              icon: <ShieldCheck />,
              disabled: busy,
              onSelect: () => {
                void patch(user, { role: Role.Admin }, `${label} is now an admin`)
              }
            },
        user.isActive
          ? {
              label: 'Deactivate',
              icon: <UserX />,
              disabled: locked || busy,
              onSelect: () => {
                void patch(user, { isActive: false }, `${label} can no longer sign in`)
              }
            }
          : {
              label: 'Activate',
              icon: <UserCheck />,
              disabled: busy,
              onSelect: () => {
                void patch(user, { isActive: true }, `${label} can sign in again`)
              }
            },
        { separator: true },
        {
          label: 'Reset password',
          icon: <KeyRound />,
          disabled: busy,
          onSelect: () => setResetTarget(user)
        },
        { separator: true },
        {
          label: 'Delete user',
          icon: <Trash2 />,
          danger: true,
          disabled: locked || busy,
          onSelect: () => setDeleteTarget(user)
        }
      ]

      return { user, isSelf, restriction: restrictionFor(isSelf, isLastAdmin), items, busy }
    })
  }, [activeAdmins, busyId, me?.id, patch, users])

  const total = users?.length ?? 0

  return (
    <section aria-labelledby="admin-users-heading" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 id="admin-users-heading" className="text-base font-semibold text-fg">
            Users
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            {users === null
              ? 'Everyone who can sign in to this instance.'
              : `${formatNumber(total)} ${pluralize(total, 'account')} · ${formatNumber(activeAdmins)} active ${pluralize(activeAdmins, 'admin')}. Sites, SSH keys and jobs always stay private to the account that owns them.`}
          </p>
        </div>
        <Button size="sm" iconLeft={<UserPlus />} onClick={() => setInviteOpen(true)}>
          New user
        </Button>
      </div>

      <Card className="overflow-hidden">
        {error ? (
          <EmptyState
            icon={<Users />}
            title="Could not load users"
            description={error}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  void load()
                }}
              >
                Try again
              </Button>
            }
          />
        ) : users === null ? (
          <div className="space-y-3 p-5">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Users />}
            title="No accounts yet"
            description="Create the first one and hand the credentials over yourself — Argus sends no email."
            action={
              <Button iconLeft={<UserPlus />} onClick={() => setInviteOpen(true)}>
                New user
              </Button>
            }
          />
        ) : (
          <>
            {/* Below md the table becomes stacked cards: a seven-column table on a
                phone can only ever scroll sideways. */}
            <ul className="divide-y divide-line md:hidden">
              {rows.map((row) => (
                <li key={row.user.id} className="flex items-start gap-3 px-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-fg">
                      {displayName(row.user)}
                    </p>
                    <p className="truncate text-xs text-fg-muted">{row.user.email}</p>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <RoleBadge role={row.user.role} />
                      <StateBadge active={row.user.isActive} />
                      {row.isSelf ? <Badge size="sm">You</Badge> : null}
                    </div>

                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-subtle">
                      <div className="flex gap-1">
                        <dt className="font-medium">Created</dt>
                        <dd>{relativeTime(row.user.createdAt)}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="font-medium">Last login</dt>
                        <dd>{relativeTime(row.user.lastLoginAt)}</dd>
                      </div>
                    </dl>
                  </div>
                  <RowActions row={row} />
                </li>
              ))}
            </ul>

            <div className="hidden md:block">
              <table className="w-full table-fixed border-collapse text-sm">
                <caption className="sr-only">Accounts on this Argus instance</caption>
                <thead>
                  <tr className="border-b border-line bg-sunken/50 text-left">
                    <th
                      scope="col"
                      className="w-[22%] px-4 py-2.5 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="w-[26%] px-4 py-2.5 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="w-[10%] px-4 py-2.5 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase"
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="w-[13%] px-4 py-2.5 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase"
                    >
                      State
                    </th>
                    <th
                      scope="col"
                      className="hidden w-[12%] px-4 py-2.5 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase lg:table-cell"
                    >
                      Created
                    </th>
                    <th
                      scope="col"
                      className="w-[12%] px-4 py-2.5 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase"
                    >
                      Last login
                    </th>
                    <th scope="col" className="w-[9%] px-4 py-2.5">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.user.id} className="border-b border-line last:border-0">
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium text-fg">
                            {displayName(row.user)}
                          </span>
                          {row.isSelf ? <Badge size="sm">You</Badge> : null}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="block truncate text-fg-muted">{row.user.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge role={row.user.role} />
                      </td>
                      <td className="px-4 py-3">
                        <StateBadge active={row.user.isActive} />
                      </td>
                      <td className="hidden px-4 py-3 text-xs whitespace-nowrap text-fg-subtle lg:table-cell">
                        {relativeTime(row.user.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap text-fg-subtle">
                        {relativeTime(row.user.lastLoginAt)}
                      </td>
                      <td className="px-4 py-3">
                        <RowActions row={row} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => {
          void load()
        }}
      />

      <ResetPasswordDialog user={resetTarget} onClose={() => setResetTarget(null)} />

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title="Delete this account?"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteTarget !== null && busyId === deleteTarget.id}
              onClick={() => {
                void remove()
              }}
            >
              Delete account
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <div className="space-y-3 text-sm leading-relaxed text-fg-muted">
            <p>
              <span className="font-semibold text-fg">{displayName(deleteTarget)}</span> (
              {deleteTarget.email}) will be removed, and everything the account owns goes with it:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>every site they connected, plus the media Argus fetched for those sites</li>
              <li>their SSH keys, including the encrypted private keys</li>
              <li>their whole job history — fetches, generations and saves</li>
            </ul>
            <p>
              Alt text already written to WordPress stays on WordPress; this only clears it out of
              Argus. There is no undo. If you just want to stop them signing in, deactivate the
              account instead.
            </p>
          </div>
        ) : null}
      </Modal>
    </section>
  )
}

export { UsersTable }
